import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "@tanstack/react-router";
import { PageShell, PageHero } from "@project/user/components/page-shell";
import {
  CountUp,
  Reveal,
  RevealText,
  Stagger,
  StaggerItem,
} from "@project/user/components/motion";
import Marquee from "react-fast-marquee";
import { Button } from "@project/components/ui/button";
import { Badge } from "@project/components/ui/badge";
import {
  Stethoscope,
  Activity,
  Building2,
  Award,
  PhoneCall,
  Eye,
  Smile,
  Sparkles,
  ChevronRight,
  UserCheck,
  HeartPulse,
  HeartHandshake,
  ShieldCheck,
  Quote,
  Phone,
  Heart,
  FileCheck,
} from "lucide-react";
import medicalImg from "@project/assets/home-medical.webp";
import {
  hospitalProfileService,
  medicalStaffService,
} from "@project/services/admin";
import { HealthcareDonationModal } from "@project/user/components/healthcare-donation-modal";
import { MahavirFoundationBankDetails } from "@project/user/components/mahavir-foundation-bank-details";
import { useI18n } from "@project/lib/use-i18n";
import type { HospitalProfile } from "@project/types/hospital-profile";
import type { MedicalStaff } from "@project/types/medical-staff";

function getUnitIcon(name: string) {
  const lowercaseName = (name || "").toLowerCase();
  if (
    lowercaseName.includes("eye") ||
    lowercaseName.includes("ophthalm") ||
    lowercaseName.includes("નેત્ર") ||
    lowercaseName.includes("આંખ")
  ) {
    return Eye;
  }
  if (
    lowercaseName.includes("dental") ||
    lowercaseName.includes("dentist") ||
    lowercaseName.includes("oral") ||
    lowercaseName.includes("દાંત") ||
    lowercaseName.includes("દંત")
  ) {
    return Smile;
  }
  if (
    lowercaseName.includes("physio") ||
    lowercaseName.includes("rehab") ||
    lowercaseName.includes("ફિઝિયો")
  ) {
    return Activity;
  }
  return Stethoscope; // default
}

export default function HealthcareIndexPage() {
  const { t, lang } = useI18n();
  const [profiles, setProfiles] = useState<HospitalProfile[]>([]);
  const [staffList, setStaffList] = useState<MedicalStaff[]>([]);
  const [donationOpen, setDonationOpen] = useState(false);
  const [selectedAmount, setSelectedAmount] = useState<number | undefined>(
    undefined
  );
  const [selectedSchemeName, setSelectedSchemeName] = useState<
    string | undefined
  >(undefined);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const healthcareStats = useMemo(
    () => [
      {
        labelKey: "care.healthcare.stats.cataract",
        value: 125,
        prefix: "",
        suffix: "+",
        icon: Sparkles,
      },
      {
        labelKey: "care.healthcare.stats.eyeOpd",
        value: 150,
        prefix: "~",
        suffix: "+",
        icon: Eye,
      },
      {
        labelKey: "care.healthcare.stats.dentalOpd",
        value: 100,
        prefix: "~",
        suffix: "+",
        icon: Smile,
      },
      {
        labelKey: "care.healthcare.stats.physioIpd",
        value: 60,
        prefix: "~",
        suffix: "+",
        icon: Activity,
      },
      {
        labelKey: "care.healthcare.stats.eyeCamps",
        value: 200,
        prefix: "",
        suffix: "+",
        icon: Award,
      },
    ],
    []
  );

  const pillarSchemes = useMemo(
    () => [
      {
        nameKey: "care.healthcare.schemes.items.mainPillar",
        amount: 351000,
        amountFormatted: lang === "gu" ? "રૂ. 3,51,000" : "₹3,51,000",
        badge: lang === "gu" ? "મુખ્ય સ્તંભ" : "Main Pillar",
        highlight: true,
      },
      {
        nameKey: "care.healthcare.schemes.items.coPillar",
        amount: 211000,
        amountFormatted: lang === "gu" ? "રૂ. 2,11,000" : "₹2,11,000",
        badge: lang === "gu" ? "સહ સ્તંભ" : "Co-Pillar",
        highlight: false,
      },
      {
        nameKey: "care.healthcare.schemes.items.lifetimeTithi",
        amount: 151000,
        amountFormatted: lang === "gu" ? "રૂ. 1,51,000" : "₹1,51,000",
        badge: lang === "gu" ? "આજીવન" : "Lifetime",
        highlight: false,
      },
      {
        nameKey: "care.healthcare.schemes.items.tenYearTithi",
        amount: 71000,
        amountFormatted: lang === "gu" ? "રૂ. 71,000" : "₹71,000",
        badge: lang === "gu" ? "૧૦ વર્ષ" : "10 Years",
        highlight: false,
      },
    ],
    [lang]
  );

  const regularSchemes = useMemo(
    () => [
      {
        nameKey: "care.healthcare.schemes.items.generalDaily",
        amount: 21000,
        amountFormatted: lang === "gu" ? "રૂ. 21,000" : "₹21,000",
        badge: lang === "gu" ? "સર્વ સેવા" : "All Services",
        highlight: true,
      },
      {
        nameKey: "care.healthcare.schemes.items.monthly",
        amount: 12000,
        amountFormatted: lang === "gu" ? "રૂ. 12,000" : "₹12,000",
        badge: lang === "gu" ? "માસિક" : "Monthly",
        highlight: false,
      },
      {
        nameKey: "care.healthcare.schemes.items.oneDay",
        amount: 5100,
        amountFormatted: lang === "gu" ? "રૂ. 5,100" : "₹5,100",
        badge: lang === "gu" ? "૧ દિવસ" : "1 Day",
        highlight: false,
      },
      {
        nameKey: "care.healthcare.schemes.items.elderMedicalAid",
        amount: 2500,
        amountFormatted: lang === "gu" ? "રૂ. 2,500" : "₹2,500",
        badge: lang === "gu" ? "દવા સહાય" : "Medicine Aid",
        highlight: false,
      },
      {
        nameKey: "care.healthcare.schemes.items.elderDiaper",
        amount: 1000,
        amountFormatted: lang === "gu" ? "રૂ. 1,000" : "₹1,000",
        badge: lang === "gu" ? "ડાયપર યોજના" : "Diaper Aid",
        highlight: false,
      },
    ],
    [lang]
  );

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [profilesRes, staffRes] = await Promise.all([
          hospitalProfileService.listPage({ page: 1, page_size: 100 }),
          medicalStaffService.listPage({ page: 1, page_size: 100 }),
        ]);
        setProfiles(
          (profilesRes.data || [])
            .filter(p => p.is_active)
            .sort((a, b) => (a.order_by ?? 0) - (b.order_by ?? 0))
        );
        setStaffList(staffRes.data || []);
      } catch (err) {
        console.error("Failed to load healthcare page data", err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const highlightedStaff = useMemo(() => {
    return staffList.find(s => s.is_highlighted && s.is_active);
  }, [staffList]);

  const handleSelectSection = (id: number) => {
    navigate({
      to: "/healthcare/$id" as any,
      params: { id: String(id) } as any,
    });
  };

  const handleOpenSponsorModal = (amount?: number, schemeName?: string) => {
    setSelectedAmount(amount);
    setSelectedSchemeName(schemeName);
    setDonationOpen(true);
  };

  if (loading) {
    return (
      <PageShell>
        <div className="flex min-h-[50vh] items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell>
      {/* HERO SECTION */}
      <PageHero
        eyebrow={t("care.healthcare.hero.eyebrow")}
        title={t("care.healthcare.hero.title")}
        subtitle={t("care.healthcare.hero.subtitle")}
        image={medicalImg}
      >
        <div className="flex flex-wrap items-center justify-center gap-3 pt-10">
          <Button
            size="lg"
            onClick={() => {
              const el = document.getElementById("sections-selector");
              el?.scrollIntoView({ behavior: "smooth" });
            }}
            className="btn-lift gap-2 bg-primary text-sm font-semibold text-primary-foreground shadow-sm"
          >
            <Building2 className="h-4 w-4" />{" "}
            {t("care.healthcare.hero.exploreBtn")}
          </Button>
          <Button
            size="lg"
            variant="secondary"
            onClick={() => handleOpenSponsorModal()}
            className="hover:bg-primary/10 backdrop-blur-xs btn-lift gap-2 bg-gold text-sm font-semibold text-foreground"
          >
            <HeartHandshake className="h-4 w-4 text-primary" />{" "}
            {t("care.healthcare.hero.donateBtn")}
          </Button>
        </div>
      </PageHero>

      <div className="mx-auto max-w-site space-y-16 px-4 py-12">
        {/* ============================================================ */}
        {/* 1. ABOUT HEALTHCARE SECTION */}
        {/* ============================================================ */}
        <section className="shadow-xs rounded-3xl border border-border bg-card p-8 md:p-10">
          <div className="grid items-center gap-8 md:grid-cols-2 md:divide-x md:divide-border">
            {/* Left Column */}
            <Reveal variant="left" className="space-y-2 md:pr-8">
              <span className="text-xs font-bold uppercase tracking-widest text-primary">
                {t("care.healthcare.about.eyebrow")}
              </span>
              <h2
                className={`font-bold tracking-tight text-foreground ${lang === "gu"
                  ? "!font-sans text-2xl sm:text-3xl"
                  : "font-serif text-3xl sm:text-4xl"
                  }`}
              >
                <RevealText delay={150}>
                  {t("care.healthcare.about.title")}
                </RevealText>
              </h2>
              <p className="text-xs leading-relaxed text-muted-foreground sm:text-sm">
                {t("care.healthcare.about.tagline")}
              </p>
            </Reveal>

            {/* Right Column */}
            <Reveal variant="right" delay={150} className="space-y-4 md:pl-8">
              <p className="whitespace-pre-line text-xs leading-relaxed text-muted-foreground sm:text-sm">
                {t("care.healthcare.about.body")}
              </p>
            </Reveal>
          </div>
        </section>

        {/* ============================================================ */}
        {/* 2. TRUSTED HEALTHCARE IMPACT ACROSS GUJARAT (METRICS) */}
        {/* ============================================================ */}
        <section className="overflow-hidden py-4 sm:py-6">
          <p className="text-center text-xs font-bold uppercase tracking-[0.22em] text-accent">
            {t("care.healthcare.stats.eyebrow")}
          </p>
          <Reveal variant="fade" className="mt-6 overflow-hidden">
            <Marquee pauseOnHover speed={35} gradient={false} className="py-2">
              {[0, 1].flatMap(pass =>
                healthcareStats.map(stat => (
                  <div
                    key={`${pass}-${stat.labelKey}`}
                    className="card-lift mx-2.5 flex h-[96px] w-[280px] shrink-0 items-center gap-4 rounded-2xl border border-border bg-card px-5 shadow-sm transition-all duration-300 hover:border-primary/50 sm:mx-3 sm:w-[300px]"
                  >
                    <div className="bg-primary/10 grid h-12 w-12 shrink-0 place-items-center rounded-xl text-primary">
                      <HeartPulse className="h-5 w-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-serif text-2xl font-semibold leading-tight text-foreground sm:text-3xl">
                        <CountUp to={stat.value} duration={1600} />
                        {stat.suffix}
                      </div>
                      <div className="mt-1 line-clamp-2 text-xs font-medium leading-snug text-muted-foreground">
                        {t(stat.labelKey)}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </Marquee>
          </Reveal>
        </section>

        {/* ============================================================ */}
        {/* 3. SELECT A HEALTHCARE UNIT (MAIN MEDICAL UNITS GRID) */}
        {/* ============================================================ */}
        <section id="sections-selector" className="scroll-mt-20 space-y-6">
          <div className="mx-auto max-w-2xl space-y-2 text-center">
            <Reveal variant="morph">
              <div className="mx-auto max-w-3xl text-center">
                <p className="text-xs font-bold uppercase tracking-[0.22em] text-accent">
                  {t("care.healthcare.units.eyebrow")}
                </p>
                <h2
                  className={`mt-3 font-semibold text-foreground ${lang === "gu"
                    ? "!font-sans text-2xl sm:text-3xl"
                    : "font-serif text-3xl sm:text-4xl"
                    }`}
                >
                  {t("care.healthcare.units.title")}
                </h2>
                <p className="mt-3 text-muted-foreground">
                  {t("care.healthcare.units.subtitle")}
                </p>
              </div>
            </Reveal>
          </div>

          {profiles.length === 0 ? (
            <div className="rounded-3xl border border-dashed border-border py-12 text-center">
              <Stethoscope className="mx-auto h-12 w-12 animate-pulse text-muted-foreground" />
              <h3 className="mt-4 font-serif text-lg font-bold text-foreground">
                {t("care.healthcare.units.emptyTitle")}
              </h3>
              <p className="mt-1 text-sm text-muted-foreground">
                {t("care.healthcare.units.emptyDesc")}
              </p>
            </div>
          ) : (
            <Stagger className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {profiles.map((sec, idx) => {
                const Icon = getUnitIcon(sec.hospital_name);
                const badgeText =
                  sec.highlights?.[0]?.title ||
                  t("care.healthcare.units.defaultBadge");
                return (
                  <StaggerItem key={sec.id} index={idx} className="grid">
                    <button
                      type="button"
                      onClick={() => handleSelectSection(sec.id)}
                      className="border-border/80 hover:shadow-xs group relative flex cursor-pointer flex-col justify-between space-y-4 overflow-hidden rounded-2xl border bg-white p-6 text-left shadow-xl transition-all duration-300 hover:border-amber-500/30 hover:to-amber-500/15"
                    >
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="shadow-2xs flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border bg-[#ffbd3d] text-gray-800 transition-transform duration-300 group-hover:scale-110">
                            <Icon className="h-6 w-6" />
                          </div>
                          <Badge
                            variant="secondary"
                            className="border-green-800 bg-green-100 px-2 py-0.5 text-[10px] font-bold text-green-800 transition-colors group-hover:bg-green-200 group-hover:text-green-900"
                          >
                            {badgeText}
                          </Badge>
                        </div>

                        <div className="space-y-1">
                          <h3
                            className={`font-bold leading-snug text-foreground transition-colors group-hover:text-[#0052b4] ${lang === "gu"
                              ? "!font-sans text-lg"
                              : "font-serif text-base"
                              }`}
                          >
                            {sec.hospital_name}
                          </h3>
                          <p className="line-clamp-2 text-xs font-medium leading-relaxed text-muted-foreground">
                            {sec.subtitle}
                          </p>
                        </div>
                      </div>

                      <div className="border-border/50 flex items-center justify-between border-t pt-2 text-xs font-bold">
                        <span className="text-primary">
                          {t("care.healthcare.units.clickToView")}
                        </span>
                        <ChevronRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-1" />
                      </div>
                    </button>
                  </StaggerItem>
                );
              })}
            </Stagger>
          )}
        </section>



        {/* ============================================================ */}
        {/* 6. BHAGWAN MAHAVIR MEDICAL CENTRE – MAINTENANCE SCHEMES */}
        {/* ============================================================ */}
        <section id="maintenance-schemes" className="scroll-mt-20 space-y-10">
          <Reveal variant="morph">
            <div className="mx-auto max-w-3xl space-y-2.5 text-center">
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-accent">
                {t("care.healthcare.schemes.eyebrow")}
              </p>
              <h2
                className={`font-semibold text-foreground ${lang === "gu"
                  ? "!font-sans text-2xl sm:text-3xl"
                  : "font-serif text-3xl sm:text-4xl"
                  }`}
              >
                {t("care.healthcare.schemes.title")}
              </h2>
              <p className="mx-auto max-w-2xl text-xs leading-relaxed text-muted-foreground sm:text-sm">
                {t("care.healthcare.schemes.subtitle")}
              </p>
            </div>
          </Reveal>

          {/* Scheme Tier 1: Major Pillar & Long-Term Schemes */}
          <div className="space-y-4">
            <Reveal
              variant="wipe-left"
              className="flex items-center gap-2 border-b border-border pb-2.5"
            >
              <Award className="h-4 w-4 text-primary" />
              <h3 className="text-sm font-bold text-foreground sm:text-base">
                {t("care.healthcare.schemes.pillarTitle")}
              </h3>
            </Reveal>

            <Stagger className="grid items-stretch gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {pillarSchemes.map((scheme, idx) => (
                <StaggerItem
                  key={scheme.nameKey}
                  index={idx}
                  from="zoom"
                  className="h-full"
                >
                  <div className="hover:border-primary/40 relative flex h-full flex-col justify-between rounded-2xl border border-border bg-card p-5 transition-all duration-300 hover:shadow-md">
                    <div className="flex flex-1 flex-col justify-between space-y-3">
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <Badge
                            variant="secondary"
                            className="px-2 py-0.5 text-[10px] font-bold"
                          >
                            {scheme.badge}
                          </Badge>
                          <Heart className="text-primary/70 h-4 w-4" />
                        </div>

                        <h4 className="!text-xl !font-sans font-bold leading-snug text-foreground">
                          {t(scheme.nameKey)}
                        </h4>
                      </div>

                      <div className="mt-3 !font-sans !text-xl font-bold text-primary sm:text-3xl">
                        {scheme.amountFormatted}
                      </div>
                    </div>

                    <Button
                      type="button"
                      size="sm"
                      onClick={() =>
                        handleOpenSponsorModal(scheme.amount, t(scheme.nameKey))
                      }
                      className="shadow-xs hover:bg-primary/90 btn-lift mt-5 w-full gap-1.5 rounded-xl bg-primary text-xs font-bold text-primary-foreground"
                    >
                      <HeartHandshake className="h-3.5 w-3.5" />
                      {t("care.healthcare.schemes.sponsorBtn")}
                    </Button>
                  </div>
                </StaggerItem>
              ))}
            </Stagger>
          </div>

          {/* Scheme Tier 2: General & Regular Medical Seva Schemes */}
          <div className="space-y-4 pt-4">
            <Reveal
              variant="wipe-left"
              className="flex items-center gap-2 border-b border-border pb-2.5"
            >
              <HeartPulse className="h-4 w-4 text-rose-500" />
              <h3 className="text-sm font-bold text-foreground sm:text-base">
                {t("care.healthcare.schemes.regularTitle")}
              </h3>
            </Reveal>

            <Stagger
              step={100}
              className="grid items-stretch gap-4 sm:grid-cols-2 lg:grid-cols-5"
            >
              {regularSchemes.map((scheme, idx) => (
                <StaggerItem
                  key={scheme.nameKey}
                  index={idx}
                  className="h-full"
                >
                  <div className="hover:border-primary/40 relative flex h-full flex-col justify-between rounded-2xl border border-border bg-card p-4 transition-all duration-300 hover:shadow-md sm:p-5">
                    <div className="flex flex-1 flex-col justify-between space-y-3">
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <Badge
                            variant="secondary"
                            className="border border-amber-500/20 bg-amber-500/10 px-2 py-0.5 text-[10px] font-bold text-amber-800"
                          >
                            {scheme.badge}
                          </Badge>
                          <HeartPulse className="h-3.5 w-3.5 text-amber-600" />
                        </div>

                        <h4 className="!text-xl !font-sans leading-snug text-foreground sm:text-sm">
                          {t(scheme.nameKey)}
                        </h4>
                      </div>

                      <div className="mt-2 !font-sans !text-xl font-bold text-primary sm:text-2xl">
                        {scheme.amountFormatted}
                      </div>
                    </div>

                    <Button
                      type="button"
                      size="sm"
                      onClick={() =>
                        handleOpenSponsorModal(scheme.amount, t(scheme.nameKey))
                      }
                      className="shadow-xs hover:bg-primary/90 btn-lift mt-4 w-full gap-1.5 rounded-xl bg-primary text-xs font-bold text-primary-foreground"
                    >
                      <HeartHandshake className="h-3.5 w-3.5" />
                      {t("care.healthcare.schemes.sponsorBtn")}
                    </Button>
                  </div>
                </StaggerItem>
              ))}
            </Stagger>
          </div>
        </section>

        {/* ============================================================ */}
        {/* 7. DIRECT CONTACT NUMBERS & HEARTFELT APPEAL SECTION */}
        {/* ============================================================ */}
        <section className="space-y-6">
          <Reveal variant="morph">
            <div className="border-primary/20 overflow-hidden rounded-3xl border bg-gradient-to-br from-card via-card to-amber-500/[0.05] p-6 shadow-lg sm:p-10">
              <div className="grid items-center gap-8 lg:grid-cols-[1.5fr_1fr]">
                {/* Left: Emotional Appeal & Motto */}
                <Reveal variant="left" delay={150} className="space-y-5">
                  <div className="inline-flex items-center gap-2 rounded-full border border-rose-500/30 bg-rose-500/10 px-3.5 py-1 text-xs font-bold text-rose-700">
                    <Heart className="h-3.5 w-3.5 fill-rose-500 text-rose-500" />
                    <span>
                      {lang === "gu" ? "ભાવુક અપીલ" : "Heartfelt Appeal"}
                    </span>
                  </div>

                  <blockquote className="border-l-4 border-primary pl-4 text-base font-medium italic leading-relaxed text-foreground sm:text-lg">
                    {t("care.healthcare.appeal.quote")}
                  </blockquote>

                  {/* 80G & CSR Badge Strip */}
                  <div className="flex flex-wrap items-center gap-3 pt-2">
                    <div className="flex items-center gap-2 rounded-xl border border-emerald-600/30 bg-emerald-500/10 px-3.5 py-2 text-xs font-bold text-emerald-800">
                      <ShieldCheck className="h-4 w-4 text-emerald-700" />
                      <span>{t("care.healthcare.appeal.taxExemptTitle")}</span>
                    </div>
                    <div className="border-primary/30 bg-primary/10 flex items-center gap-2 rounded-xl border px-3.5 py-2 text-xs font-bold text-primary">
                      <FileCheck className="h-4 w-4 text-primary" />
                      <span>
                        {lang === "gu"
                          ? "CSR ફંડ સ્વીકાર્ય છે"
                          : "CSR Funds Accepted"}
                      </span>
                    </div>
                  </div>

                  <p className="text-xs text-muted-foreground">
                    {t("care.healthcare.appeal.taxExemptDesc")}
                  </p>

                  {/* Motto Banner */}
                  <div className="bg-secondary/30 rounded-xl border border-border p-3.5 text-xs font-bold text-foreground">
                    <Quote className="-mt-0.5 mr-1.5 inline h-3.5 w-3.5 text-primary" />
                    <span>{t("care.healthcare.appeal.motto")}</span>
                  </div>
                </Reveal>

                {/* Right: Direct Contact Helpdesk Card */}
                <Reveal
                  variant="right"
                  delay={250}
                  className="border-primary/30 space-y-4 rounded-2xl border bg-card p-6 shadow-md"
                >
                  <div className="flex items-center gap-2.5 border-b border-border pb-3">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary text-primary-foreground">
                      <PhoneCall className="h-4 w-4" />
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-foreground">
                        {t("care.healthcare.appeal.helplineTitle")}
                      </h4>
                      <p className="text-[11px] text-muted-foreground">
                        {lang === "gu"
                          ? "અચૂક સંપર્ક કરવા નમ્ર નિવેદન"
                          : "Feel free to call anytime for seva support"}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-2.5">
                    <a
                      href="tel:8758413564"
                      className="border-border/80 group flex items-center justify-between rounded-xl border bg-background p-3 text-xs font-bold text-foreground transition-all hover:border-primary hover:text-primary"
                    >
                      <span className="flex items-center gap-2">
                        <Phone className="h-3.5 w-3.5 text-primary transition-transform group-hover:scale-110" />
                        87584 13564
                      </span>
                      <ChevronRight className="h-3.5 w-3.5 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
                    </a>

                    <a
                      href="tel:9725521170"
                      className="border-border/80 group flex items-center justify-between rounded-xl border bg-background p-3 text-xs font-bold text-foreground transition-all hover:border-primary hover:text-primary"
                    >
                      <span className="flex items-center gap-2">
                        <Phone className="h-3.5 w-3.5 text-primary transition-transform group-hover:scale-110" />
                        97255 21170
                      </span>
                      <ChevronRight className="h-3.5 w-3.5 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
                    </a>

                    <a
                      href="tel:9099930582"
                      className="border-border/80 group flex items-center justify-between rounded-xl border bg-background p-3 text-xs font-bold text-foreground transition-all hover:border-primary hover:text-primary"
                    >
                      <span className="flex items-center gap-2">
                        <Phone className="h-3.5 w-3.5 text-primary transition-transform group-hover:scale-110" />
                        90999 30582
                      </span>
                      <ChevronRight className="h-3.5 w-3.5 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
                    </a>
                  </div>

                  <Button
                    type="button"
                    onClick={() => handleOpenSponsorModal()}
                    className="shadow-xs btn-lift h-10 w-full gap-2 rounded-xl bg-primary text-xs font-bold text-primary-foreground"
                  >
                    <HeartHandshake className="h-4 w-4" />
                    {lang === "gu"
                      ? "આરોગ્ય સહાય દાન આપો"
                      : "Donate to Healthcare"}
                  </Button>
                </Reveal>
              </div>
            </div>
          </Reveal>
        </section>

        {/* ============================================================ */}
        {/* 5. DOCTORS DETAILS HIGHLIGHTED & HELPLINE STRIP */}
        {/* ============================================================ */}
        <Reveal variant="zoom">
          <section
            id="contact-healthcare"
            className="border-primary/20 shadow-xs mx-auto max-w-5xl rounded-2xl border bg-card p-6 sm:p-7"
          >
            <div className="flex flex-col items-center justify-between gap-6 md:flex-row">
              {/* Medical Incharge */}
              <div className="flex min-w-0 items-center gap-3.5">
                <div className="bg-primary/10 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-primary">
                  <UserCheck className="h-5 w-5" />
                </div>
                <div className="min-w-0 text-xs sm:text-sm">
                  <span className="block text-[11px] font-bold uppercase text-muted-foreground sm:mr-2 sm:inline">
                    {t("care.healthcare.contact.inchargeLabel")}
                  </span>
                  <span className="font-serif text-sm font-bold text-foreground sm:text-base">
                    {highlightedStaff
                      ? `${highlightedStaff.name} (${highlightedStaff.designation})`
                      : t("care.healthcare.contact.defaultIncharge")}
                  </span>
                </div>
              </div>

              <div className="bg-border/60 hidden h-9 w-px md:block" />

              {/* Helpline */}
              <div className="flex min-w-0 items-center gap-3.5">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-500/15 text-amber-700">
                  <PhoneCall className="h-5 w-5" />
                </div>
                <div className="min-w-0 text-xs sm:text-sm">
                  <span className="block text-[11px] font-bold uppercase text-amber-700 sm:mr-2 sm:inline">
                    {t("care.healthcare.contact.helplineLabel")}
                  </span>
                  <a
                    href="tel:9978930950"
                    className="text-sm font-black text-amber-700 hover:underline sm:text-base"
                  >
                    9978930950
                  </a>
                </div>
              </div>
            </div>
          </section>
        </Reveal>
      </div>

      {/* Bank & UPI Details */}
      <MahavirFoundationBankDetails />

      {/* Donation Modal with Preloaded Scheme & Amount */}
      <HealthcareDonationModal
        open={donationOpen}
        onOpenChange={open => {
          setDonationOpen(open);
          if (!open) {
            setSelectedAmount(undefined);
            setSelectedSchemeName(undefined);
          }
        }}
        initialAmount={selectedAmount}
        maintenance_schemes={selectedSchemeName}
      />
    </PageShell>
  );
}
